import React, { useState, useEffect, useMemo, useRef } from "react";
import { Divider, Modal, Input, List, Checkbox, message } from "antd";
import {
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { api } from "@redux/api";

const TagList = ({ allTags, list, deleteTag }) => {
  return (
    <>
      {list.map((tag) => (
        <div
          key={tag}
          className="inline-flex items-center px-3 py-1 m-1 text-base bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
        >
          <TagOutlined className="mr-1 text-gray-500" />
          <span className="mr-1">
            {allTags.find((item) => item.value === tag)?.label || tag}
          </span>
          <CloseOutlined
            className="ml-1 text-xs text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              deleteTag(tag);
            }}
          />
        </div>
      ))}
    </>
  );
};

const GlobalTag = ({ tableName, recordId, facility }) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchValueInModal, setSearchValueInModal] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [key, setKey] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(240);

  // 初始化标签数据
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 获取所有可用标签
        const tagsResponse = await api.getCustomtags();
        if (tagsResponse.status === 200) {
          const tags = tagsResponse.data.results.map((item) => ({
            label: item.name,
            value: item.name, // 使用标签名称作为value
          }));
          setAllTags(tags);
        }

        // 获取当前记录的标签
        if (tableName && recordId) {
          try {
            const recordTagsResponse = await api.getRecordTagsByEndpoint(
              tableName,
              recordId
            );
            if (
              recordTagsResponse.status === 200 &&
              recordTagsResponse.data.tags
            ) {
              setSelectedTags(recordTagsResponse.data.tags);
            }
          } catch (error) {
            console.error("Error fetching record tags:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [tableName, recordId]);

  // 更新下拉菜单宽度
  useEffect(() => {
    if (inputContainerRef.current) {
      const updateWidth = () => {
        const containerWidth = inputContainerRef.current.offsetWidth;
        setDropdownWidth(Math.max(containerWidth, 240));
      };

      updateWidth();
      window.addEventListener("resize", updateWidth);

      return () => {
        window.removeEventListener("resize", updateWidth);
      };
    }
  }, [isInlineEditing, isFocused]);

  // 确保下拉菜单在视口内可见
  useEffect(() => {
    if (isFocused && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.bottom > viewportHeight) {
        dropdown.style.top = "auto";
        dropdown.style.bottom = "100%";
        dropdown.style.marginTop = "0";
        dropdown.style.marginBottom = "8px";
      }
    }
  }, [isFocused]);

  const options = useMemo(() => {
    return allTags.filter(
      (item) =>
        item.label.toLowerCase().includes(searchValue.toLowerCase()) &&
        !selectedTags.includes(item.value)
    );
  }, [allTags, searchValue, selectedTags]);

  const canCreateTag =
    searchValue.length > 0 &&
    !allTags.some((item) => item.label === searchValue);

  // 保存标签到服务器
  const saveTagsToServer = async (tags) => {
    if (!tableName || !recordId) return;

    setIsSaving(true);
    try {
      // 发送标签名称数组
      const response = await api.updateRecordTags(tableName, recordId, tags);

      if (response.status >= 200 && response.status < 300) {
        message.success("Tags saved successfully");
      } else {
        message.error("Failed to save tags");
      }
    } catch (error) {
      console.error("Error saving tags:", error);
      message.error("Failed to save tags");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateTag = () => {
    if (!searchValue.trim()) return;

    // 创建新标签
    const newTagData = {
      name: searchValue,
      table_name: tableName,
    };

    api
      .createCustomtag(tableName, newTagData)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          const newTag = {
            label: response.data.name,
            value: response.data.name, // 使用标签名称作为value
          };

          // 更新标签列表和选中标签
          setAllTags([...allTags, newTag]);
          const newSelectedTags = [...selectedTags, newTag.value];
          setSelectedTags(newSelectedTags);

          // 保存关联
          saveTagsToServer(newSelectedTags);
        }
      })
      .catch((error) => {
        console.error("Error creating tag:", error);
        message.error("Failed to create tag");
      });

    setSearchValue("");
    setKey(key + 1);
  };

  const startNewTag = (e) => {
    e.stopPropagation();
    setIsInlineEditing(true);
    setIsFocused(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleCreateModalTag = () => {
    if (!searchValueInModal.trim()) return;

    // 创建新标签
    const newTagData = {
      name: searchValueInModal,
      table_name: tableName,
    };

    api
      .createCustomtag(tableName, newTagData)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          const newTag = {
            label: response.data.name,
            value: response.data.name, // 使用标签名称作为value
          };

          // 更新标签列表和选中标签
          setAllTags([...allTags, newTag]);
          const newSelectedTags = [...selectedTags, newTag.value];
          setSelectedTags(newSelectedTags);

          // 保存关联
          saveTagsToServer(newSelectedTags);
        }
      })
      .catch((error) => {
        console.error("Error creating tag:", error);
        message.error("Failed to create tag");
      });

    setSearchValueInModal("");
  };

  const deleteTag = (tag) => {
    const newTags = selectedTags.filter((item) => item !== tag);
    setSelectedTags(newTags);
    saveTagsToServer(newTags);
  };

  const filteredTags = useMemo(() => {
    if (!searchValueInModal) {
      return allTags;
    }
    return allTags.filter((item) =>
      item.label.toLowerCase().includes(searchValueInModal.toLowerCase())
    );
  }, [allTags, searchValueInModal]);

  const needCreateModalTag =
    searchValueInModal.length > 0 &&
    !allTags.some((item) => item.label === searchValueInModal);

  const onSelect = (value) => {
    const newTags = [...selectedTags, value];
    setSelectedTags(newTags);
    setIsFocused(false);
    setIsInlineEditing(false);
    saveTagsToServer(newTags);
  };

  const handleClickTagArea = () => {
    setIsInlineEditing(true);
    setIsFocused(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.trim()) {
      handleCreateTag();
    } else if (e.key === "Escape") {
      setIsInlineEditing(false);
      setIsFocused(false);
      setSearchValue("");
    }
  };

  const handleModalSave = () => {
    saveTagsToServer(selectedTags);
    setIsEditModalOpen(false);
  };

  // 处理点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".tag-input-container")) {
        setIsFocused(false);
        if (!searchValue.trim()) {
          setIsInlineEditing(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchValue]);

  return (
    <>
      <Modal
        open={isEditModalOpen}
        title="Edit Tags"
        onCancel={() => setIsEditModalOpen(false)}
        onOk={handleModalSave}
        width="90%"
        okText="Save"
        confirmLoading={isSaving}
      >
        <div className="text-gray-500 mb-4">
          Select tags to add or remove from this record.
        </div>

        <div className="mb-4">
          <TagList
            allTags={allTags}
            list={selectedTags}
            deleteTag={deleteTag}
          />
        </div>

        <Input
          value={searchValueInModal}
          onChange={(e) => setSearchValueInModal(e.target.value)}
          placeholder="Find tags or enter a new tag name..."
          suffix={<SearchOutlined />}
          allowClear
          className="mb-4"
          size="large"
          style={{ fontSize: "16px" }}
        />

        {needCreateModalTag && (
          <div
            className="flex items-center p-2 cursor-pointer hover:bg-gray-100 rounded-md mb-2"
            onClick={handleCreateModalTag}
          >
            <PlusOutlined className="text-blue-500" />
            <div className="pl-4 text-base">
              Create tag "{searchValueInModal}"
            </div>
          </div>
        )}

        <List
          dataSource={filteredTags}
          renderItem={(item) => (
            <div className="flex items-center p-2 cursor-pointer hover:bg-gray-100 rounded-md">
              <Checkbox
                checked={selectedTags.includes(item.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedTags([...selectedTags, item.value]);
                  } else {
                    setSelectedTags(
                      selectedTags.filter((tag) => tag !== item.value)
                    );
                  }
                }}
                className="text-base"
              >
                <span className="pl-2 text-base">{item.label}</span>
              </Checkbox>
            </div>
          )}
        />
      </Modal>

      <div
        ref={containerRef}
        className="relative border border-gray-200 rounded-full px-3 py-2 flex items-center flex-wrap bg-white hover:border-blue-400 transition-colors"
        style={{ fontSize: "16px" }}
      >
        <TagOutlined className="text-blue-500 mr-2" />

        <div
          className="flex-grow flex flex-wrap items-center cursor-text min-h-[32px]"
          onClick={handleClickTagArea}
        >
          <TagList
            allTags={allTags}
            list={selectedTags}
            deleteTag={deleteTag}
          />

          {isInlineEditing && (
            <div
              ref={inputContainerRef}
              className="relative tag-input-container inline-block"
              style={{ zIndex: 1000 }}
            >
              <Input
                ref={inputRef}
                key={key}
                bordered={false}
                className="w-40 pl-0"
                style={{ fontSize: "16px" }}
                placeholder="Type a tag name..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onKeyDown={handleKeyDown}
                suffix={null}
                autoFocus
                size="large"
              />

              {isFocused && (
                <div
                  ref={dropdownRef}
                  className="absolute left-0 top-full mt-1 bg-white shadow-lg rounded-md z-50 border border-gray-200"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    width: `${dropdownWidth}px`,
                    overflowX: "hidden",
                  }}
                >
                  {canCreateTag && (
                    <div
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-blue-500"
                      onClick={handleCreateTag}
                      style={{ fontSize: "16px" }}
                    >
                      <PlusOutlined className="mr-2" />
                      <span>Create "{searchValue}"</span>
                    </div>
                  )}

                  {(options.length > 0 || canCreateTag) && (
                    <Divider className="my-1" />
                  )}

                  {options.length > 0
                    ? options.map((option) => (
                        <div
                          key={option.value}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer whitespace-normal"
                          onClick={() => onSelect(option.value)}
                          style={{ fontSize: "16px" }}
                        >
                          {option.label}
                        </div>
                      ))
                    : !canCreateTag && (
                        <div
                          className="px-3 py-2 text-gray-500"
                          style={{ fontSize: "16px" }}
                        >
                          No tags found
                        </div>
                      )}
                </div>
              )}
            </div>
          )}

          {!isInlineEditing && !selectedTags.length && (
            <span className="text-gray-400" style={{ fontSize: "16px" }}>
              Click to add tags
            </span>
          )}
        </div>

        <div className="flex items-center">
          <div
            className="cursor-pointer p-1 hover:bg-gray-100 rounded-full transition-colors mr-2"
            onClick={startNewTag}
            title="Add a tag"
          >
            <PlusOutlined
              className="text-blue-500"
              style={{ fontSize: "18px" }}
            />
          </div>

          <div
            className="cursor-pointer p-1 hover:bg-gray-100 rounded-full transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditModalOpen(true);
            }}
            title="Manage tags"
          >
            <EditOutlined
              className="text-blue-500"
              style={{ fontSize: "18px" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalTag;
